import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import type { ActionWeek } from '../detail';
import { series, chrome, chartFont } from '../reportTheme';
import ChartTooltip from './ChartTooltip';

const axisTick = { fill: chrome.muted, fontSize: 11, fontFamily: chartFont };
const RAISED_COLOR = series.blue;
const RESOLVED_COLOR = '#22A06B';

interface WeeklyActionsChartProps {
  weeks: ActionWeek[];
}

const dayMonth = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

/** Two-line week label under each bar pair: "KW 41" and "06.10. – 12.10.". */
const WeekTick: React.FC<{ x?: number; y?: number; payload?: { value: number }; data: ActionWeek[]; prefix: string }> = ({
  x = 0,
  y = 0,
  payload,
  data,
  prefix,
}) => {
  const week = payload ? data[payload.value] : undefined;
  if (!week) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" {...axisTick} fill={chrome.textSecondary}>
        {`${prefix} ${week.week}`}
      </text>
      <text dy={26} textAnchor="middle" {...axisTick}>
        {`${dayMonth(week.start)} – ${dayMonth(week.end)}`}
      </text>
    </g>
  );
};

/**
 * "Actions im Zeitverlauf": raised and closed actions per calendar week as
 * bar pairs, each with its count on top while the bars are wide enough.
 */
const WeeklyActionsChart: React.FC<WeeklyActionsChartProps> = ({ weeks }) => {
  const { t } = useTranslation('templates');
  const raisedLabel = t('report.detail.actions.flow.raised');
  const resolvedLabel = t('report.detail.actions.flow.resolved');
  const rows = weeks.map((w, i) => ({ index: i, [raisedLabel]: w.raised, [resolvedLabel]: w.resolved }));
  const showValues = weeks.length <= 13;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ top: 16, right: 8, left: -16, bottom: 0 }} barGap={2}>
        <CartesianGrid stroke={chrome.grid} vertical={false} />
        <XAxis
          dataKey="index"
          tickLine={false}
          axisLine={{ stroke: chrome.axis }}
          interval={showValues ? 0 : 'preserveStartEnd'}
          height={40}
          tick={<WeekTick data={weeks} prefix={t('report.detail.actions.flow.week')} />}
        />
        <YAxis tick={axisTick} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
        <Tooltip
          content={<ChartTooltip />}
          labelFormatter={(value) => {
            const week = weeks[Number(value)];
            return week ? `${t('report.detail.actions.flow.week')} ${week.week} · ${dayMonth(week.start)} – ${dayMonth(week.end)}` : '';
          }}
          cursor={{ fill: 'rgba(45, 120, 219, 0.06)' }}
        />
        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontFamily: chartFont, fontSize: 12, color: chrome.textSecondary }} />
        <Bar dataKey={raisedLabel} fill={RAISED_COLOR} radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={false}>
          {showValues && <LabelList dataKey={raisedLabel} position="top" style={{ ...axisTick, fill: chrome.textSecondary, fontWeight: 600 }} />}
        </Bar>
        <Bar dataKey={resolvedLabel} fill={RESOLVED_COLOR} radius={[3, 3, 0, 0]} maxBarSize={22} isAnimationActive={false}>
          {showValues && <LabelList dataKey={resolvedLabel} position="top" style={{ ...axisTick, fill: chrome.textSecondary, fontWeight: 600 }} />}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default WeeklyActionsChart;
